import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  Cancel01Icon,
  SettingsIcon,
  HelpCircleIcon,
  CreditCardIcon,
  BookOpen02Icon,
  File01Icon,
} from "@hugeicons/core-free-icons";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { createProjectsApi } from "@/lib/api/projects";

function formatEditedTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } else if (days > 0) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  } else {
    return "Just now";
  }
}

const commands = [
  {
    slug: "/docs",
    title: "Documentation",
    description: "Open Rayloop documentation",
    icon: BookOpen02Icon,
    action: "external",
    url: "https://docs.rayloop.ai",
  },
  {
    slug: "/settings",
    title: "Settings",
    description: "Open workspace settings",
    icon: SettingsIcon,
    action: "navigate",
    url: "/settings",
  },
  {
    slug: "/billing",
    title: "Billing",
    description: "Manage your subscription and billing",
    icon: CreditCardIcon,
    action: "navigate",
    url: "/settings",
  },
  {
    slug: "/help",
    title: "Help & Support",
    description: "Get help and contact support",
    icon: HelpCircleIcon,
    action: "external",
    url: "https://rayloop.ai/help",
  },
];

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const projectsApi = createProjectsApi({ getToken });

  const isCommandMode = query.startsWith("/");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: searchData, isLoading } = useQuery({
    queryKey: ["projects", "search", debouncedQuery],
    queryFn: () =>
      projectsApi.list({
        search: debouncedQuery || undefined,
        sortBy: "last_viewed_at",
        sortOrder: "desc",
        limit: 5,
      }),
    enabled: open && !isCommandMode,
  });

  const filteredProjects = searchData?.items ?? [];

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Keyboard shortcut to open (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onOpenChange]);

  const filteredCommands = commands.filter(
    (c) =>
      c.slug.includes(query.toLowerCase()) ||
      c.title.toLowerCase().includes(query.toLowerCase()),
  );

  const handleProjectClick = (projectId: string) => {
    onOpenChange(false);
    navigate(`/projects/${projectId}`);
  };

  const handleCommandClick = (cmd: (typeof commands)[number]) => {
    onOpenChange(false);
    if (cmd.action === "external") {
      window.open(cmd.url, "_blank");
    } else {
      navigate(cmd.url);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        style={{
          maxWidth: "42rem",
          padding: 0,
          gap: 0,
        }}
        className="overflow-hidden shadow-2xl"
      >
        <DialogTitle className="sr-only">
          Search projects or type / for commands
        </DialogTitle>

        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4">
          <HugeiconsIcon
            icon={Search01Icon}
            className="size-5 shrink-0 text-muted-foreground"
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search projects or type / for commands"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-lg text-foreground placeholder:text-muted-foreground outline-none"
          />
          <button
            onClick={() => onOpenChange(false)}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <HugeiconsIcon
              icon={Cancel01Icon}
              className="size-5"
              strokeWidth={2}
            />
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-3">
          {isCommandMode ? (
            /* Commands mode */
            (filteredCommands.length > 0 && (<div>
              <div className="px-5 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Commands
              </div>
              {filteredCommands.map((cmd) => (
                <button
                  key={cmd.slug}
                  onClick={() => handleCommandClick(cmd)}
                  className="flex w-full items-center gap-4 px-5 py-3 text-left hover:bg-accent transition-colors"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border/50 text-muted-foreground">
                    <HugeiconsIcon icon={cmd.icon} className="size-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground font-mono">
                        {cmd.slug}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {cmd.title}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {cmd.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>))
          ) : (
            /* Recent projects mode */
            (<>
              {isLoading ? (
                <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                  Loading projects…
                </div>
              ) : filteredProjects.length > 0 ? (
                <div>
                  <div className="px-5 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Recent Projects
                  </div>
                  {filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="flex w-full items-center gap-4 px-5 py-3 text-left cursor-pointer hover:bg-accent transition-colors"
                    >
                      {/* Icon */}
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/30 text-muted-foreground">
                        <HugeiconsIcon icon={File01Icon} className="size-4" />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {project.name}
                        </p>
                      </div>
                      {/* Time */}
                      <span className="shrink-0 text-sm text-muted-foreground">
                        {formatEditedTime(project.updatedAt)}
                      </span>
                    </button>
                  ))}
                </div>
              ) : query ? (
                <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                  No projects found for "{query}"
                </div>
              ) : (
                <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                  No projects yet
                </div>
              )}
            </>)
          )}

          {isCommandMode && filteredCommands.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No commands found
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
